import { useEffect, useRef, useCallback } from "react";

export const useIdleAutoRefresh = (
  onRefresh,
  { idleTimeout = 60_000, refreshInterval = 300_000, enabled = true } = {}
) => {
  const callbackRef = useRef(onRefresh);
  const idleTimerRef = useRef(null);
  const intervalRef = useRef(null);
  const catchUpTimerRef = useRef(null);
  const lastActivityRef = useRef(0);

  const lastRefreshAtRef = useRef(Date.now());

  const isIdleRef = useRef(false);

  useEffect(() => {
    callbackRef.current = onRefresh;
  }, [onRefresh]);

  const fireRefresh = useCallback(() => {
    lastRefreshAtRef.current = Date.now();
    callbackRef.current?.();
  }, []);

  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (catchUpTimerRef.current) {
      clearTimeout(catchUpTimerRef.current);
      catchUpTimerRef.current = null;
    }
  }, []);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    isIdleRef.current = false;

    idleTimerRef.current = setTimeout(() => {
      isIdleRef.current = true;

      fireRefresh();

      intervalRef.current = setInterval(() => {
        fireRefresh();
      }, refreshInterval);
    }, idleTimeout);
  }, [clearAllTimers, fireRefresh, idleTimeout, refreshInterval]);

  useEffect(() => {
    if (!enabled) return;

    const ACTIVITY_EVENTS = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "scroll",
      "wheel",
      "pointerdown",
    ];

    const handleUserActivity = () => {
      const now = Date.now();

      if (now - lastActivityRef.current < 200) return;

      lastActivityRef.current = now;

      startIdleTimer();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        clearAllTimers();
        return;
      }

      const now = Date.now();
      const elapsed = now - lastRefreshAtRef.current;

      if (elapsed >= refreshInterval) {
        fireRefresh();
        startIdleTimer();
      } else {
        const remaining = refreshInterval - elapsed;

        catchUpTimerRef.current = setTimeout(() => {
          fireRefresh();
          startIdleTimer();
        }, remaining);
      }
    };

    lastRefreshAtRef.current = Date.now();
    startIdleTimer();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleUserActivity, { passive: true })
    );
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearAllTimers();
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleUserActivity)
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, startIdleTimer, clearAllTimers, fireRefresh, refreshInterval]);
};
